/* eslint-disable max-len */
import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema } from '../Header';
import DownloadBox from './DownloadBox';

const defaultThumbnailURL =
    'https://icon-library.com/images/google-docs-icon-png/google-docs-icon-png-29.jpg';

const schema: JSONSchema7 = {
    title: 'Download Box',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        isMultipleDownload: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Download multiple documents',
        },
        showHeader: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Show Header',
        },
        dataKey: {
            type: 'string',
            title: 'Data Key',
            default: '',
        },
        downloadOptions: {
            type: 'object',
            title: 'Download Options',
            properties: {
                showDocumentThumbnail: {
                    type: 'boolean',
                    enum: [true, false],
                    title: 'Show Document Thumbnail',
                    default: true,
                },
                documentNameDatakey: {
                    type: 'string',
                    title: 'Data key for document name',
                    default: 'name',
                },
                isHideDocumentCheckboxes: {
                    type: 'boolean',
                    title: 'Hide checkboxes for documents',
                    default: false,
                },
                isHideShareIcons: {
                    type: 'boolean',
                    title: 'Hide share icons for documents',
                    default: false,
                },
                isHideDownloadIcons: {
                    type: 'boolean',
                    title: 'Hide download icons for documents',
                    default: false,
                },
                downloadLinkDatakey: {
                    type: 'string',
                    title: 'Data key for document link',
                    default: 'url',
                },
                downloadIconType: {
                    type: 'string',
                    enum: ['Font-awesome', 'Icon-URL'],
                    title: 'Download Icon Type',
                    default: 'Font-awesome',
                },
                downloadIconPosition: {
                    type: 'string',
                    title: 'Download Icon Position',
                    enum: ['Right', 'Left'],
                    default: 'Right',
                },
            },
            dependencies: {
                showDocumentThumbnail: {
                    oneOf: [
                        {
                            properties: {
                                showDocumentThumbnail: { enum: [true] },
                                documentThumbnailPosition: {
                                    type: 'string',
                                    title: 'Document Thumbnail Position',
                                    enum: ['Right', 'Left'],
                                    default: 'Left',
                                },
                                documentThumbnailURLDatakey: {
                                    type: 'string',
                                    title: 'Data key for document thumbnail URL',
                                    default: 'code',
                                },
                                defaultThumbnailURL: {
                                    type: 'string',
                                    title: 'Default thumbnail URL',
                                    default: defaultThumbnailURL,
                                },
                            },
                        },
                        {
                            properties: {
                                showDocumentThumbnail: { enum: [false] },
                            },
                        },
                    ],
                },
                downloadIconType: {
                    oneOf: [
                        {
                            properties: {
                                downloadIconType: { enum: ['Font-awesome'] },
                                downloadIconName: {
                                    type: 'string',
                                    title: 'Download Icon Name',
                                    default: 'fas fa-download',
                                },
                            },
                            required: ['downloadIconName'],
                        },
                        {
                            properties: {
                                downloadIconType: { enum: ['Icon-URL'] },
                                downloadIconURL: {
                                    type: 'string',
                                    title: 'Download Icon URL',
                                    default: '',
                                },
                            },
                            required: ['downloadIconURL'],
                        },
                    ],
                },
            },
        },
        shareOptions: {
            type: 'object',
            title: 'Share Options',
            properties: {
                shareIconType: {
                    type: 'string',
                    enum: ['Font-awesome', 'Icon-URL'],
                    title: 'Share Icon Type',
                    default: 'Font-awesome',
                },
                shareLinkDatakey: {
                    type: 'string',
                    title: 'Data key for share link',
                    default: 'url',
                },
                shareLinkTitle: {
                    type: 'string',
                    title: 'Data key for share link title',
                    default: 'Share via Email',
                },
            },
            dependencies: {
                shareIconType: {
                    oneOf: [
                        {
                            properties: {
                                shareIconType: { enum: ['Font-awesome'] },
                                shareIconName: {
                                    type: 'string',
                                    title: 'Share Icon Name',
                                    default: 'fas fa-share-alt',
                                },
                            },
                            required: ['shareIconName'],
                        },
                        {
                            properties: {
                                shareIconType: { enum: ['Icon-URL'] },
                                shareIconURL: {
                                    type: 'string',
                                    title: 'Share Icon URL',
                                    default: '',
                                },
                            },
                            required: ['shareIconURL'],
                        },
                    ],
                },
            },
        },
        sortDocumentsBy: {
            type: 'string',
            title: 'Sort Documents By',
            description: 'Enter document codes separated by comma',
            default: '',
        },
        isTransformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
    },
    dependencies: {
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
        isMultipleDownload: {
            oneOf: [
                {
                    properties: {
                        isMultipleDownload: { enum: [true] },
                        packageName: {
                            type: 'string',
                            title: 'Download Package Name',
                            default: 'pdf-package',
                        },
                        downloadAllButtonOption: {
                            type: 'object',
                            title: 'Download All Button Option',
                            properties: {
                                buttonName: {
                                    type: 'string',
                                    title: 'Download All Button Name',
                                },
                                showIcon: {
                                    type: 'boolean',
                                    enum: [true, false],
                                    description: 'Show Icon on side of button name or not',
                                    default: false,
                                    title: 'Show Icon',
                                },
                            },
                            dependencies: {
                                showIcon: {
                                    oneOf: [
                                        {
                                            properties: {
                                                showIcon: { enum: [true] },
                                                iconType: {
                                                    type: 'string',
                                                    enum: ['Font-awesome', 'Icon-URL'],
                                                    title: 'Download All Button Icon Type',
                                                    default: 'Font-awesome',
                                                },
                                                iconPosition: {
                                                    type: 'string',
                                                    title: 'Icon Position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Left',
                                                },
                                            },
                                            required: ['iconType'],
                                            dependencies: {
                                                iconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                iconType: {
                                                                    enum: ['Font-awesome'],
                                                                },
                                                                iconName: {
                                                                    type: 'string',
                                                                    title: 'Download All Button Icon Name',
                                                                    default: 'fas fa-plus-circle',
                                                                },
                                                            },
                                                            required: ['iconName'],
                                                        },
                                                        {
                                                            properties: {
                                                                iconType: { enum: ['Icon-URL'] },
                                                                iconURL: {
                                                                    type: 'string',
                                                                    title: 'Download All Button Icon URL',
                                                                    default: '',
                                                                },
                                                            },
                                                            required: ['iconURL'],
                                                        },
                                                    ],
                                                },
                                            },
                                        },
                                        {
                                            properties: {
                                                showIcon: { enum: [false] },
                                            },
                                            required: ['linkName'],
                                        },
                                    ],
                                },
                            },
                        },
                        downloadPackageLinkText: {
                            type: 'string',
                            title: 'Download Package Link ready text',
                            default: 'Click here',
                        },
                        downloadPackageText: {
                            type: 'string',
                            title: 'Download Package ready text',
                            default: 'to download package.',
                        },
                        downloadPackageMessage: {
                            type: 'string',
                            title: 'Download Package Time Message',
                            default: 'Downloading package may take a few minutes.',
                        },
                        downloadPackageError: {
                            type: 'string',
                            title: 'Download Package Error Message',
                            default: 'An error occured while generating package.',
                        },
                    },
                    required: ['packageName'],
                },
                {
                    properties: {
                        isMultipleDownload: { enum: [false] },
                    },
                },
            ],
        },
        showHeader: {
            oneOf: [
                {
                    properties: {
                        showHeader: { enum: [true] },
                        headerOption: {
                            type: 'object',
                            title: 'Header Option',
                            properties: {
                                headerTitle: {
                                    ...headingsSchema,
                                    title: '',
                                    minItems: 1,
                                    maxItems: 1,
                                },
                                headerIcon: {
                                    type: 'boolean',
                                    enum: [true, false],
                                    title: 'Show header icon',
                                    default: false,
                                },
                            },
                            dependencies: {
                                headerIcon: {
                                    oneOf: [
                                        {
                                            properties: {
                                                headerIcon: { enum: [true] },
                                                headerIconType: {
                                                    type: 'string',
                                                    enum: ['Font-awesome', 'Icon-URL'],
                                                    title: 'Header Icon Type',
                                                    default: 'Font-awesome',
                                                },
                                                headerIconPosition: {
                                                    type: 'string',
                                                    title: 'Header Icon Position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Left',
                                                },
                                            },
                                            dependencies: {
                                                headerIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                headerIconType: {
                                                                    enum: ['Font-awesome'],
                                                                },
                                                                headerIconName: {
                                                                    type: 'string',
                                                                    title: 'Header Icon Name',
                                                                    default: 'far fa-file',
                                                                },
                                                            },
                                                            required: ['headerIconName'],
                                                        },
                                                        {
                                                            properties: {
                                                                headerIconType: {
                                                                    enum: ['Icon-URL'],
                                                                },
                                                                headerIconURL: {
                                                                    type: 'string',
                                                                    title: 'Header Icon URL',
                                                                    default: '',
                                                                },
                                                            },
                                                            required: ['headerIconURL'],
                                                        },
                                                    ],
                                                },
                                            },
                                        },
                                        {
                                            properties: {
                                                headerIcon: { enum: [false] },
                                            },
                                        },
                                    ],
                                },
                            },
                        },
                    },
                },
                {
                    properties: {
                        showHeader: { enum: [false] },
                    },
                },
            ],
        },
        isTransformData: {
            oneOf: [
                {
                    properties: {
                        isTransformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default:
                                // eslint-disable-next-line max-len
                                'return (data) => { return {transformedData} }',
                        },
                    },
                },
            ],
        },
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'isMultipleDownload',
            'packageName',
            'downloadAllButtonOption',
            'downloadPackageLinkText',
            'downloadPackageText',
            'downloadPackageMessage',
            'downloadPackageError',
            'showHeader',
            'headerOption',
            'dataKey',
            'downloadOptions',
            'shareOptions',
            'sortDocumentsBy',
            'iconType',
            'iconPosition',
            'iconName',
            'iconURL',
            'headerIconType',
            'headerIconPosition',
            'headerIconName',
            'headerIconURL',
        ],
        'Transform Data': ['isTransformData', 'functionToTransform'],
    },
];

const DownloadBoxWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        headerOption: {
            headerTitle: {
                'ui:options': { removable: false },
            },
        },
        isTransformData: { 'ui:widget': 'CustomCheckbox' },
        functionToTransform: { 'ui:widget': 'JSEditor' },
    },
    templateView: DownloadBox,
    id: 'DownloadBox',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        showHeader: true,
        headerOption: {
            headerTitle: [
                {
                    headingLevel: 6,
                    headerText: 'Documents',
                },
            ],
        },
    }),
};

export default DownloadBoxWidget;
